import { FC, useState, MouseEvent } from "react";

import { useNavigate } from "react-router-dom";
import { ColumnsType } from "antd/es/table";
import { Table, Button, message } from "antd";

import { RouteLinks } from "services/router.service";
import { CompanyItem, Loading } from "components/common";
import { getContractorsSelector } from "store/contractors/contractors.selector";
import { IContractor } from "store/contractors/contractors.types";
import { formatPhoneNumber } from "services/data.service";
import { useAppSelector, useAppDispatch } from "store";
import { getContractors } from "store/contractors/contractors.thunks";
import deleteIcon from "data/icons/trash.svg";
import api from "services/api.service";
import { ContractorsDeleteModal } from "components/contractors";

import "./ContractorsTable.scss";

const ContractorsTable: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { contractors, isContractorsLoading } = useAppSelector(getContractorsSelector);
  const [ deletingContractor, setDeletingContractor ] = useState<IContractor | null>(null);

  const onDeleteContractor = (contractor: IContractor, e: MouseEvent) => {
    e.stopPropagation();
    setDeletingContractor(contractor);
  };

  const handleDeleteContractor = async () => {
    if (!deletingContractor) return;

    try {
      await api.deleteContractor(deletingContractor);
      message.success("Contractor deleted successfully!");
      setDeletingContractor(null);
      dispatch(getContractors());
    } catch (e: any) {
      message.error(e.response?.data?.error || e.error || "Something went wrong!");
    }
  };

  const columns: ColumnsType<IContractor> = [
    {
      title: "Official name",
      dataIndex: "official_name",
      className: "official-name-cell",
      key: "official_name",
      onHeaderCell: () => ({
        id: "products-header-cell",
      }),
      width: 200,
      render: (value, item) => (
        <CompanyItem
          email={ item.default_email }
          has_account={ item.has_account }
          has_verified_account={ item.has_verified_account }
          official_name={ item.official_name }
          phone={ item.phone_number || "" }
          value={ value }
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      onHeaderCell: () => ({
        id: "products-header-cell",
      }),
      width: 200,
      render: (value) => <p className="active">{value}</p>,
    },
    {
      title: "Default email",
      dataIndex: "default_email",
      key: "default_email",
      onHeaderCell: () => ({
        id: "products-header-cell",
      }),
      width: 200,
    },
    {
      title: "Phone number",
      dataIndex: "phone_number",
      key: "phone_number",
      onHeaderCell: () => ({
        id: "products-header-cell",
      }),
      width: 200,
      render: (value) => value && formatPhoneNumber(value),
    },
    {
      title: "",
      dataIndex: "action_delete",
      key: "action_delete",
      width: "2%",
      render: (_value, item) => (
        <Button
          type="text"
          size="small"
          className="team-table-delete-button"
          icon={ <img src={ deleteIcon } alt=""/> }
          onClick={ (e) => onDeleteContractor(item, e) }
        >
        </Button>
      ),
    },
  ]

  return (
    <>
      <Loading isLoading={ isContractorsLoading } isColored isFullWidth height={ 200 }>
        <Table
          className="cases-table products-table"
          id="products-table"
          columns={ columns }
          dataSource={ contractors }
          pagination={ false }
          rowKey={ (item) => item.id }
          scroll={ { y: window.innerHeight - 233 } }
          tableLayout="auto"
          onRow={ (record) => ({
            onClick: () => navigate(`${ RouteLinks.PROTECTED.CONTRACTORS.LIST }/${ record.id }`),
          }) }
        />
      </Loading>

      <ContractorsDeleteModal
        deletingContractor={ deletingContractor }
        onCancel={ () => setDeletingContractor(null) }
        onConfirm={ handleDeleteContractor }
      />
    </>
  )
}

export default ContractorsTable;
